import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Flame, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return `${BACKEND_URL}${src}`;
};

// Hand-picked "best sellers" across popular categories
const BESTSELLER_IDS = ['TL037', 'TL032', 'TL002', 'TL022', 'TL024', 'TL051', 'TL052', 'TL001', 'TL023', 'TL045'];

const BestSellers = () => {
  const navigate = useNavigate();
  const scrollerRef = useRef(null);
  const [tools, setTools] = useState([]);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/tools/`)
      .then((res) => {
        if (!mounted) return;
        const all = res.data || [];
        const picked = BESTSELLER_IDS.map((id) => all.find((t) => t.id === id)).filter(Boolean);
        setTools(picked.length ? picked : all.slice(0, 10));
      })
      .catch(() => mounted && setTools([]));
    return () => { mounted = false; };
  }, []);

  const scroll = (dir) => {
    scrollerRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  if (tools.length === 0) return null;

  return (
    <section
      id="mas-vendidos"
      data-testid="best-sellers-section"
      className="relative py-20 bg-slate-900 overflow-hidden"
    >
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-red-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <span className="inline-flex items-center gap-2 bg-red-600/20 text-red-400 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
              <Flame size={14} /> Los favoritos
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
              Los más <span className="text-red-500">vendidos</span>
            </h2>
            <p className="text-slate-400 mt-2 max-w-xl">
              Las herramientas que más eligen los profesionales del drywall.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              data-testid="best-sellers-prev"
              onClick={() => scroll(-1)}
              aria-label="Anterior"
              className="w-11 h-11 rounded-full border border-slate-700 text-slate-300 hover:bg-red-600 hover:border-red-600 hover:text-white transition-colors flex items-center justify-center"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="best-sellers-next"
              onClick={() => scroll(1)}
              aria-label="Siguiente"
              className="w-11 h-11 rounded-full border border-slate-700 text-slate-300 hover:bg-red-600 hover:border-red-600 hover:text-white transition-colors flex items-center justify-center"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Carousel */}
        <div
          ref={scrollerRef}
          data-testid="best-sellers-carousel"
          className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none' }}
        >
          {tools.map((tool, idx) => (
            <div
              key={tool.id}
              data-testid={`best-seller-${tool.id}`}
              onClick={() => navigate(`/tienda/producto/${tool.id}`)}
              className="group snap-start shrink-0 w-[240px] sm:w-[260px] bg-white rounded-2xl overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="relative">
                <div
                  className="aspect-[4/3] bg-slate-100 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                  style={{ backgroundImage: `url(${resolveImg(tool.image)})` }}
                />
                {idx < 3 && (
                  <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1">
                    <Flame size={11} /> Top {idx + 1}
                  </span>
                )}
              </div>
              <div className="p-4">
                <span className="text-[10px] uppercase tracking-wide text-red-600 font-bold">{tool.id}</span>
                <h3 className="text-sm font-semibold text-slate-900 line-clamp-2 leading-tight min-h-[2.5rem] mt-1 group-hover:text-red-600 transition-colors">
                  {tool.name}
                </h3>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xl font-bold text-slate-900">${tool.price_usd?.toFixed(2)}</span>
                  <span className="text-xs text-red-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    Ver <ArrowRight size={12} />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BestSellers;
